/**
 * The sample company behind "Ukázat na ukázkových datech": three months of Fio
 * payments ending today, receipts the AI matched to most of them, issued
 * invoices paid by clients, a few receipts still waiting for a payment and a
 * few payments still missing a document.
 *
 * Every merchant, client and amount is invented. Ids are fixed UUIDs in the
 * `00000000-0000-4000-8000-…` range, so they look like real rows to the app
 * but can never be confused with one. Deterministic for a given day (seeded),
 * tested in `src/lib/demo/__tests__/fixtures.test.ts`.
 */
import type { Row } from "./postgrest";

export type DemoTables = Record<string, Row[]>;

/** A fixed, recognisable UUID per sample row. */
export function demoId(n: number): string {
  return `00000000-0000-4000-8000-${String(n).padStart(12, "0")}`;
}

export const DEMO_ACCOUNT_ID = demoId(1);

const pad = (n: number) => String(n).padStart(2, "0");
const iso = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;

/** Card payments and transfers out: description, amount, currency, merchant. */
const SPENDS: readonly [string, number, string, string][] = [
  ["Platba kartou: KANCELAR PLUS, Praha", -1290, "CZK", "Kancelář Plus s.r.o."],
  ["Datacentrum Morava a.s. — hosting", -890, "CZK", "Datacentrum Morava a.s."],
  ["Platba kartou: CERPACI STANICE U LESA", -1860, "CZK", "Čerpací stanice U Lesa"],
  ["Software Atelier GmbH — subscription", -29, "EUR", "Software Atelier GmbH"],
  ["Platba kartou: KAVARNA NA ROHU", -245, "CZK", "Kavárna Na Rohu"],
  ["Telekom Nova a.s. — vyúčtování", -799, "CZK", "Telekom Nova a.s."],
  ["Platba kartou: ELEKTRO SVET, Brno", -6990, "CZK", "Elektro Svět s.r.o."],
  ["Platba kartou: TAXI PRAHA SEVER", -380, "CZK", "Taxi Praha Sever"],
  ["Cloudly Ltd. — storage", -12, "EUR", "Cloudly Ltd."],
  ["Tiskárna Vltava s.r.o. — vizitky", -3450, "CZK", "Tiskárna Vltava s.r.o."],
  ["Platba kartou: PAPIRNICTVI U ZASTAVKY", -186, "CZK", "Papírnictví U Zastávky"],
];

const RENT: [string, number, string, string] = [
  "Nájem kanceláře Karlín",
  -14000,
  "CZK",
  "Karlínské kanceláře s.r.o.",
];

const CLIENTS: readonly [string, string, string][] = [
  ["Studio Horizont s.r.o.", "Praha", "CZK"],
  ["Pekárna Zlatý klas s.r.o.", "Olomouc", "CZK"],
  ["Alpenblick Design GmbH", "Wien", "EUR"],
];

function seeded(seed: number) {
  let s = seed;
  return () => (s = (s * 1103515245 + 12345) % 2147483648) / 2147483648;
}

/** The three calendar months ending with `today`'s, oldest first. */
function lastMonths(today: Date): { year: number; month: number; open: boolean }[] {
  const out = [];
  for (let back = 2; back >= 0; back--) {
    const d = new Date(today.getFullYear(), today.getMonth() - back, 1);
    out.push({ year: d.getFullYear(), month: d.getMonth() + 1, open: back === 0 });
  }
  return out;
}

const daysIn = (y: number, m: number) => new Date(y, m, 0).getDate();

/**
 * The sample tables, keyed by table name. `organization_id` and `user_id` are
 * left out: the backend ignores those filters, so the rows belong to whichever
 * company is open.
 */
export function buildDemoTables(today: Date = new Date()): DemoTables {
  const rnd = seeded(20261104);
  let n = 100;
  const next = () => demoId(++n);

  const transactions: Row[] = [];
  const receipts: Row[] = [];
  const matches: Row[] = [];
  const invoices: Row[] = [];

  const clients: Row[] = CLIENTS.map(([name, city, currency], i) => ({
    id: demoId(10 + i),
    name,
    company_name: name,
    city,
    country: city === "Wien" ? "AT" : "CZ",
    email: null,
    ico: null,
    dic: null,
    default_currency: currency,
    default_due_days: 14,
    created_at: "2026-01-05T09:00:00Z",
    updated_at: "2026-01-05T09:00:00Z",
  }));

  const bankAccount: Row = {
    id: DEMO_ACCOUNT_ID,
    name: "Fio – běžný účet",
    bank: "fio",
    bank_name: "Fio banka",
    bank_code: "2010",
    account_number: "2900000000",
    iban: null,
    swift: null,
    country: "CZ",
    currency: "CZK",
    api_token: "",
    last_sync_at: `${iso(today.getFullYear(), today.getMonth() + 1, today.getDate())}T05:00:00Z`,
    sync_error: null,
    sync_error_since: null,
    sync_mode: null,
    token_saved_at: null,
    fio_last_initialized: true,
    created_at: "2026-01-05T09:00:00Z",
    updated_at: "2026-01-05T09:00:00Z",
  };

  const tx = (date: string, fields: Row): Row => {
    const row: Row = {
      id: next(),
      date,
      category: null,
      notes: null,
      notes_encrypted: null,
      description_encrypted: null,
      invoice_id: null,
      receipt_id: null,
      variable_symbol: null,
      counter_account: null,
      counter_bank_code: null,
      owner_split: "shared",
      shared_petr_percent: 50,
      status: null,
      matched: false,
      created_at: `${date}T06:00:00Z`,
      updated_at: `${date}T06:00:00Z`,
      ...fields,
    };
    transactions.push(row);
    return row;
  };

  const receipt = (date: string, vendor: string, amount: number, currency: string): Row => {
    const id = next();
    const row: Row = {
      id,
      vendor,
      vendor_encrypted: null,
      amount,
      currency,
      issue_date: date,
      description: null,
      description_encrypted: null,
      file_name: `${vendor
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")}_${date}.pdf`,
      // No file behind it: previews say the file is not available in sample data.
      file_url: null,
      matched: false,
      transaction_id: null,
      match_score: null,
      ocr_text: null,
      notes_encrypted: null,
      source: rnd() < 0.4 ? "email" : "upload",
      source_email: null,
      invoice_number: null,
      order_number: null,
      payment_reference: null,
      variable_symbol: null,
      document_group_id: null,
      document_part: null,
      created_at: `${date}T08:00:00Z`,
      updated_at: `${date}T08:00:00Z`,
    };
    receipts.push(row);
    return row;
  };

  const match = (t: Row, doc: Row, kind: "receipt" | "invoice", score: number) => {
    const row: Row = {
      id: next(),
      transaction_id: t.id,
      receipt_id: kind === "receipt" ? doc.id : null,
      invoice_id: kind === "invoice" ? doc.id : null,
      match_group_id: null,
      match_score: score,
      created_at: t.created_at,
      receipts: kind === "receipt" ? doc : null,
      invoices: kind === "invoice" ? doc : null,
      transactions: { date: t.date },
    };
    matches.push(row);
    t.matched = true;
    if (kind === "receipt") {
      t.receipt_id = doc.id;
      doc.matched = true;
      doc.transaction_id = t.id;
      doc.match_score = score;
    } else {
      t.invoice_id = doc.id;
    }
  };

  for (const { year, month, open } of lastMonths(today)) {
    const lastDay = open ? Math.max(1, today.getDate()) : daysIn(year, month);
    const day = () => 1 + Math.floor(rnd() * lastDay);
    const date = (d: number) => iso(year, month, Math.min(d, lastDay));

    // Card payments and services. Past months are nearly all matched; the open
    // month still has work left, which is what the sample is for.
    const count = open ? Math.max(4, Math.round((lastDay / 30) * 12)) : 12;
    for (let k = 0; k < count; k++) {
      const [description, amount, currency, vendor] = SPENDS[Math.floor(rnd() * SPENDS.length)];
      const d = date(day());
      const t = tx(d, { description, amount, currency, counter_name: vendor });
      const r = rnd();
      if (r < (open ? 0.55 : 0.85)) {
        match(t, receipt(d, vendor, amount, currency), "receipt", 86 + Math.floor(rnd() * 12));
      } else if (r < (open ? 0.85 : 0.95)) {
        t.status = "missing";
      }
    }

    tx(date(2), {
      description: RENT[0],
      amount: RENT[1],
      currency: RENT[2],
      counter_name: RENT[3],
      variable_symbol: `${year}${pad(month)}`,
    });
    const rent = transactions[transactions.length - 1];
    if (!open || lastDay >= 2) {
      match(rent, receipt(date(1), RENT[3], RENT[1], RENT[2]), "receipt", 100);
    }

    tx(date(lastDay), {
      description: "Poplatek za vedení účtu",
      amount: -99,
      currency: "CZK",
      counter_name: "Fio banka",
      status: "not_needed",
      matched: true,
    });

    // Issued invoices; clients pay the closed months, the open month waits.
    CLIENTS.forEach(([, , currency], k) => {
      const client = clients[k];
      const total =
        currency === "EUR"
          ? 1200 + Math.round(rnd() * 20) * 50
          : 18000 + Math.round(rnd() * 40) * 500;
      const issue = date(3 + k * 5);
      const paid = !open;
      const invoiceNumber = `${year}${pad(month)}${k + 1}`;
      const invoice: Row = {
        id: next(),
        invoice_number: invoiceNumber,
        client_id: client.id,
        clients: { name: client.name, company_name: client.company_name },
        issue_date: issue,
        taxable_date: issue,
        due_date: date(Math.min(3 + k * 5 + 14, daysIn(year, month))),
        paid_date: paid ? date(25) : null,
        sent_at: `${issue}T09:00:00Z`,
        status: paid ? "paid" : "sent",
        currency,
        subtotal: total,
        tax_amount: 0,
        total,
        amount: total,
        type: "invoice",
        category: null,
        language: "cs",
        variable_symbol: invoiceNumber,
        vendor: null,
        file_url: null,
        file_name: null,
        created_at: `${issue}T09:00:00Z`,
        updated_at: `${issue}T09:00:00Z`,
      };
      invoices.push(invoice);
      if (paid) {
        const t = tx(date(25), {
          description: `Příchozí platba: ${client.name}`,
          amount: total,
          currency,
          counter_name: client.name,
          variable_symbol: invoiceNumber,
          status: "invoice",
        });
        match(t, invoice, "invoice", 100);
      }
    });

    // Receipts forwarded this month that no payment has come in for yet.
    if (open) {
      for (let k = 0; k < 2; k++) {
        const [, amount, currency, vendor] = SPENDS[Math.floor(rnd() * SPENDS.length)];
        receipt(date(1 + k * 3), vendor, amount, currency);
      }
    }
  }

  return {
    transactions,
    receipts,
    matches,
    invoices,
    clients,
    bank_accounts: [bankAccount],
  };
}
